/**
 * KeyboardShortcutsOverlay – Modal overlay showing available keyboard shortcuts.
 *
 * Triggered by Ctrl+/ (or Cmd+/ on Mac) or the ? key.
 * Can also be opened programmatically via the `open` prop.
 */

import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  IconButton,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardIcon from "@mui/icons-material/Keyboard";

interface ShortcutItem {
  keys: string;
  description: string;
}

interface ShortcutGroup {
  title: string;
  shortcuts: ShortcutItem[];
}

const isMac =
  typeof navigator !== "undefined" &&
  /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);

const mod = isMac ? "\u2318" : "Ctrl";

const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    title: "General",
    shortcuts: [
      { keys: `${mod}+S`, description: "Save current data" },
      { keys: `${mod}+/`, description: "Show keyboard shortcuts" },
      { keys: "Escape", description: "Cancel current edit / close dialog" },
    ],
  },
  {
    title: "Data Grid Navigation",
    shortcuts: [
      { keys: "Tab", description: "Move to next editable cell" },
      { keys: "Shift+Tab", description: "Move to previous editable cell" },
      { keys: "Enter", description: "Commit edit and move down" },
      { keys: "F2", description: "Enter edit mode on selected cell" },
      { keys: "Escape", description: "Cancel cell edit (revert value)" },
      { keys: "\u2190 \u2191 \u2192 \u2193", description: "Navigate cells" },
    ],
  },
  {
    title: "Data Entry",
    shortcuts: [
      { keys: `${mod}+V`, description: "Paste from Excel / clipboard" },
      { keys: `${mod}+Z`, description: "Undo last edit" },
      { keys: `${mod}+Shift+Z`, description: "Redo last edit" },
    ],
  },
];

interface KeyboardShortcutsOverlayProps {
  open?: boolean;
  onClose?: () => void;
}

export const KeyboardShortcutsOverlay: React.FC<
  KeyboardShortcutsOverlayProps
> = ({ open: controlledOpen, onClose: controlledOnClose }) => {
  const [internalOpen, setInternalOpen] = useState(false);

  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const handleClose = useCallback(() => {
    if (isControlled && controlledOnClose) {
      controlledOnClose();
    } else {
      setInternalOpen(false);
    }
  }, [isControlled, controlledOnClose]);

  const handleOpen = useCallback(() => {
    if (!isControlled) {
      setInternalOpen(true);
    }
  }, [isControlled]);

  // Listen for Ctrl+/ or ? key
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Ctrl+/ or Cmd+/
      if ((e.ctrlKey || e.metaKey) && e.key === "/") {
        e.preventDefault();
        if (isOpen) {
          handleClose();
        } else {
          handleOpen();
        }
        return;
      }
      // ? key (not in an input)
      if (
        e.key === "?" &&
        !e.ctrlKey &&
        !e.metaKey &&
        !(e.target instanceof HTMLInputElement) &&
        !(e.target instanceof HTMLTextAreaElement)
      ) {
        e.preventDefault();
        if (isOpen) {
          handleClose();
        } else {
          handleOpen();
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, handleOpen, handleClose]);

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          maxHeight: "80vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <KeyboardIcon sx={{ color: "#6B3FA0" }} />
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#1E293B" }}>
            Keyboard Shortcuts
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {SHORTCUT_GROUPS.map((group, gi) => (
          <Box key={group.title} sx={{ mb: gi < SHORTCUT_GROUPS.length - 1 ? 2 : 0 }}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: "#6B3FA0",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                mb: 1,
              }}
            >
              {group.title}
            </Typography>
            {group.shortcuts.map((shortcut) => (
              <Box
                key={shortcut.keys}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 0.75,
                  px: 1,
                  borderRadius: "8px",
                  "&:hover": { bgcolor: "#F8FAFC" },
                }}
              >
                <Typography sx={{ fontSize: 14, color: "#475569" }}>
                  {shortcut.description}
                </Typography>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  {shortcut.keys.split("+").map((key) => (
                    <Box
                      key={key}
                      component="kbd"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 28,
                        height: 26,
                        px: 0.75,
                        borderRadius: "6px",
                        border: "1px solid #D1D5DB",
                        bgcolor: "#F9FAFB",
                        fontSize: 12,
                        fontFamily: "inherit",
                        fontWeight: 500,
                        color: "#374151",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                      }}
                    >
                      {key.trim()}
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
            {gi < SHORTCUT_GROUPS.length - 1 && <Divider sx={{ mt: 1.5 }} />}
          </Box>
        ))}

        <Box
          sx={{
            mt: 2,
            pt: 1.5,
            borderTop: "1px solid #E2E8F0",
            textAlign: "center",
          }}
        >
          <Typography sx={{ fontSize: 12, color: "#94A3B8" }}>
            Press <kbd style={{ fontWeight: 600 }}>{mod}+/</kbd> or{" "}
            <kbd style={{ fontWeight: 600 }}>?</kbd> to toggle this overlay
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
